import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Plus, Trash2 } from 'lucide-react';
import { useChat } from '../chat/ChatContext.tsx';
import { useAiPrefs } from '../lib/aiPrefs.ts';
import './SettingsScreen.css';
import './BotSettingsScreen.css';

/**
 * «Настройки бота»: which shapes the composer offers, and what tier it draws at
 * when nobody says otherwise.
 *
 * Per person rather than per conversation, which is why one screen serves both
 * bots' chats and why the route carries the chat id only to know where «назад»
 * goes. Somebody who works in vertical certificates should not have to say so
 * again in the second bot's chat.
 *
 * What is deliberately *not* here is «завершить разговор». Closing the context
 * is something done in the middle of one, and two taps into a settings screen
 * is the wrong place for it — «/новый чат» in the composer is the whole of that
 * feature. (The handoff's developer note listed it here; its own caption under
 * the same fragment says the opposite, and the caption is right.)
 */

const QUALITIES = [
  ['low', 'Черновик', 'для итераций — быстро и дёшево'],
  ['medium', 'Хорошее', 'обычный выбор'],
  ['high', 'Максимум', 'для финала'],
] as const;

/** «7:5» — two whole numbers. Mirrors the pattern in server/src/ai-prefs.ts. */
const RATIO = /^([1-9]\d{0,2}):([1-9]\d{0,2})$/;

/** A rectangle of the shape itself, beside its numbers. Same glyph the composer draws. */
function RatioGlyph({ ratio }: { ratio: string }) {
  const [w, h] = ratio.split(':').map(Number);
  const wide = Number.isFinite(w) && Number.isFinite(h) && w! > 0 && h! > 0 ? w! / h! : 1;
  const box = 28;
  return (
    <span
      className="bot-ratio-glyph"
      style={{
        width: `${wide >= 1 ? box : Math.round(box * wide)}px`,
        height: `${wide >= 1 ? Math.round(box / wide) : box}px`,
      }}
      aria-hidden="true"
    />
  );
}

export function BotSettingsScreen() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { chats } = useChat();
  const chat = chats.find((item) => item.id === chatId) ?? null;
  const { prefs, save } = useAiPrefs(true);

  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [error, setError] = useState<string | null>(null);

  const added = `${width}:${height}`;
  const canAdd = RATIO.test(added) && !prefs.ratios.includes(added);

  const store = (ratios: string[], quality: typeof prefs.quality) => {
    setError(null);
    // Optimistic inside the hook, corrected by what comes back — the server is
    // what decides a shape is readable.
    void save({ ratios, quality }).catch(() => setError('Не удалось сохранить'));
  };

  const addRatio = () => {
    if (!canAdd) return;
    store([...prefs.ratios, added], prefs.quality);
    setWidth('');
    setHeight('');
  };

  const removeRatio = (ratio: string) => {
    // The last one cannot go: this list is also the only place to add a shape,
    // so somebody who deleted their last would have taken away their own way
    // back. Refused here as well as on the server, so the button explains
    // itself rather than silently doing nothing.
    if (prefs.ratios.length <= 1) {
      setError('Хотя бы один формат должен остаться — иначе нечего будет выбрать');
      return;
    }
    store(
      prefs.ratios.filter((item) => item !== ratio),
      prefs.quality,
    );
  };

  return (
    <div className="app-frame settings-screen">
      <header className="settings-header settings-header-divided">
        <button
          type="button"
          className="icon-button settings-back"
          onClick={() => navigate(chatId ? `/chat/${chatId}` : '/')}
          aria-label="Назад"
        >
          <ChevronLeft size={22} />
        </button>
        <h2 className="settings-heading">Настройки бота</h2>
      </header>

      <div className="bot-settings-body">
        <section className="bot-section">
          <h3 className="bot-section-title">Форматы в композере</h3>
          <p className="bot-section-hint">
            Список свой: эти форматы стоят в ряду параметров, ненужные удаляются, новые задаются
            двумя числами. Модель делает картинки в нескольких размерах — что не совпадает точно,
            округляется до ближайшего, и подпись под картинкой скажет, что получилось.
          </p>

          <ul className="bot-ratios">
            {prefs.ratios.map((ratio) => (
              <li key={ratio} className="bot-ratio">
                {/* No tick, which is where this departs from the handoff. Its
                    mock shows one shape unticked, and a tick that can be taken
                    off needs somewhere to remember a shape that is kept but
                    off — a second list, for a state nobody asked for: removing
                    one and typing it back is two taps either way. So the list
                    is the enabled set, and a tick on every row would be a
                    control that cannot be operated. */}
                <span className="bot-ratio-shape">
                  <RatioGlyph ratio={ratio} />
                </span>
                <span className="bot-ratio-label">{ratio}</span>
                <span className="bot-ratio-spacer" />
                <button
                  type="button"
                  className="icon-button bot-ratio-remove"
                  onClick={() => removeRatio(ratio)}
                  aria-label={`Удалить формат ${ratio}`}
                >
                  <Trash2 size={17} />
                </button>
              </li>
            ))}
          </ul>

          <form
            className="bot-ratio-add"
            onSubmit={(event) => {
              event.preventDefault();
              addRatio();
            }}
          >
            <input
              inputMode="numeric"
              placeholder="7"
              aria-label="Ширина"
              value={width}
              onChange={(event) => setWidth(event.target.value.replace(/\D/g, '').slice(0, 3))}
            />
            <span className="bot-ratio-colon">:</span>
            <input
              inputMode="numeric"
              placeholder="5"
              aria-label="Высота"
              value={height}
              onChange={(event) => setHeight(event.target.value.replace(/\D/g, '').slice(0, 3))}
            />
            <button type="submit" className="bot-ratio-submit" disabled={!canAdd}>
              <Plus size={16} />
              Добавить формат
            </button>
          </form>
        </section>

        <section className="bot-section">
          <h3 className="bot-section-title">Качество по умолчанию</h3>
          <p className="bot-section-hint">
            Черновик — для итераций, максимум — осознанный выбор для финала. Картинку обычно просят
            два-три раза, прежде чем она получится. Это про GPT: у «Ассистента» на Gemini выбора
            качества нет — его эндпоинт такого не принимает, и все три дадут одно и то же.
          </p>

          <ul className="bot-qualities">
            {QUALITIES.map(([value, label, note]) => (
              <li key={value}>
                <button
                  type="button"
                  className="bot-quality"
                  role="radio"
                  aria-checked={prefs.quality === value}
                  onClick={() => store(prefs.ratios, value)}
                >
                  <span
                    className={`bot-radio${prefs.quality === value ? ' bot-radio-on' : ''}`}
                    aria-hidden="true"
                  />
                  <span className="bot-quality-text">
                    <b>{label}</b>
                    <small>{note}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>

        {error && <p className="bot-error">{error}</p>}

        <p className="bot-footnote">
          Настройки ваши, а не этого чата — держатся и у «{chat?.peer?.displayName ?? 'бота'}», и у
          второго ассистента. Разговор закрывается не здесь, а командой «/новый чат» в поле ввода.
        </p>
      </div>
    </div>
  );
}
